import {useMutation} from '@github-ui/react-query'
import {isStaff} from '@github-ui/stats'
import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon, LockIcon, PulseIcon, SyncIcon} from '@primer/octicons-react'
import {ActionList, Button, IconButton, Label, Link, RelativeTime, Spinner} from '@primer/react'
import {Blankslate, SkeletonText} from '@primer/react/experimental'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'

import {apiGenerateRecap} from '../../api/scribe/api-generate-recap'
import type {Recap} from '../../api/scribe/types'
import {useMarkdownPreview} from '../../hooks/use-markdown-preview'
import {useRecapsQuery} from '../../queries/use-recaps-query'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {SanitizedMarkdownViewer} from '../common/sanitized-markdown-viewer'
import contentStyles from './content.module.css'
import {SidePanelToolbar} from './header'
import styles from './recap-content.module.css'

function formatRecapDateRange(recap: Recap): {dateString: string; showWeekOf: boolean} {
  if (recap.start_time && recap.end_time) {
    const startDate = new Date(recap.start_time)
    const endDate = new Date(recap.end_time)
    const startStr = startDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})
    const endStr = endDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})
    return {dateString: `${startStr} — ${endStr}`, showWeekOf: false}
  }
  if (recap.created_at) {
    return {
      dateString: new Date(recap.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      showWeekOf: true,
    }
  }
  return {dateString: '', showWeekOf: true}
}

export const RecapContent = memo(function RecapContent() {
  const projectRouteParams = useProjectRouteParams()
  const {
    data: recaps,
    isLoading,
    error,
    refetch,
  } = useRecapsQuery({
    variables: projectRouteParams,
    refetchInterval: 5000, // Poll every 5 seconds
  })
  const [selectedTab, setSelectedTab] = useState<'latest' | 'archive'>('latest')
  const {html: reportHtml, loadPreview} = useMarkdownPreview()
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null)
  const [selectedRecapId, setSelectedRecapId] = useState<number | null>(null)

  const feedbackUrl = isStaff()
    ? 'https://gh.io/copilot-reporting-project-feedback'
    : 'https://gh.io/copilot-reporting-project-feedback-external'

  const latestRecap = useMemo(() => {
    if (selectedRecapId !== null && recaps) {
      const found = recaps.find(recap => recap.id === selectedRecapId)
      return found || null
    }
    return recaps && recaps.length ? recaps[0] : null
  }, [selectedRecapId, recaps])
  const hasValidData =
    latestRecap &&
    ((latestRecap.insights && latestRecap.insights.length > 0) || (latestRecap.report && latestRecap.report.content))

  useEffect(() => {
    if (latestRecap?.report?.content) {
      const unescapedContent = latestRecap.report.content.replace(/\\n/g, '\n')
      loadPreview(unescapedContent)
    }
  }, [latestRecap, loadPreview])

  const {mutate: generateRecap, isPending: isGeneratingRecap} = useMutation({
    mutationFn: apiGenerateRecap,
    onSuccess: () => refetch(),
  })

  const handleGenerateRecap = useCallback(() => {
    setGenerationStartTime(Date.now())
    generateRecap(projectRouteParams)
  }, [generateRecap, projectRouteParams])

  const handleViewRecap = useCallback((recapId: number) => {
    setGenerationStartTime(null)
    setSelectedRecapId(recapId)
    setSelectedTab('latest')
  }, [])

  const handleSelectLatestTab = useCallback(() => {
    setSelectedTab('latest')
    setSelectedRecapId(null)
  }, [])

  const handleSelectArchiveTab = useCallback(() => {
    setSelectedTab('archive')
  }, [])

  const isRecapRecent = useMemo(() => {
    if (!latestRecap?.created_at) return false
    if (!generationStartTime) return false
    const recapTime = new Date(latestRecap.created_at).getTime()
    return recapTime > generationStartTime
  }, [latestRecap, generationStartTime])

  const showLoadingSpinner = generationStartTime !== null && !isRecapRecent

  const noRecapsAvailable = !isLoading && !error && !hasValidData && !showLoadingSpinner

  return (
    <div className={contentStyles.infoPanelContainer} {...testIdProps('side-panel-recap-content')}>
      <div className={contentStyles.infoPanelContent}>
        <div className={contentStyles.infoPanelHeader}>
          <SidePanelToolbar showCloseButton>
            <div className={styles.infoPanelLinks}>
              {isStaff() && <Label variant="accent">Staff</Label>}
              <Link
                muted
                href={feedbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Feedback"
                className={styles.feedbackLink}
              >
                Feedback
              </Link>
            </div>
          </SidePanelToolbar>
          <div className={contentStyles.headerSpacer} />
          <h2>Recap</h2>
        </div>

        <div role="tablist" aria-label="Recap tabs" className={styles.tabList}>
          <Button
            role="tab"
            aria-selected={selectedTab === 'latest'}
            aria-controls="latest-panel"
            id="latest-tab"
            onClick={handleSelectLatestTab}
            variant={selectedTab === 'latest' ? 'default' : 'invisible'}
            className={selectedTab === 'latest' ? styles.tabActive : styles.tab}
            tabIndex={selectedTab === 'latest' ? 0 : -1}
          >
            Latest
          </Button>
          <Button
            role="tab"
            aria-selected={selectedTab === 'archive'}
            aria-controls="archive-panel"
            id="archive-tab"
            onClick={handleSelectArchiveTab}
            variant={selectedTab === 'archive' ? 'default' : 'invisible'}
            className={selectedTab === 'archive' ? styles.tabActive : styles.tab}
            tabIndex={selectedTab === 'archive' ? 0 : -1}
          >
            Archive
          </Button>
        </div>

        {selectedTab === 'latest' && (
          <div role="tabpanel" id="latest-panel" aria-labelledby="latest-tab">
            {isLoading && <div>Loading recaps...</div>}

            {showLoadingSpinner && (
              <div>
                <Button disabled leadingVisual={<Spinner size="small" />} className={styles.generateRecapLargeButton}>
                  Generating recap...
                </Button>

                <div className={styles.insightCard}>
                  <div className={styles.skeletonText}>
                    <SkeletonText lines={3} aria-label="Generating recap" />
                  </div>
                  <div className={styles.skeletonText}>
                    <SkeletonText lines={3} aria-hidden="true" />
                  </div>
                  <div className={styles.skeletonText}>
                    <SkeletonText lines={3} aria-hidden="true" />
                  </div>
                </div>
              </div>
            )}

            {hasValidData && !showLoadingSpinner && (
              <div className={styles.recapsContent}>
                {latestRecap.created_at && (
                  <div className={styles.recapHeader}>
                    <h2 className={styles.insightTitle}>
                      {(() => {
                        const {dateString, showWeekOf} = formatRecapDateRange(latestRecap)
                        return showWeekOf ? `Week of ${dateString}` : dateString
                      })()}
                      <span className={styles.recapDate}>
                        Generated <RelativeTime datetime={latestRecap.created_at} /> · <LockIcon size={12} /> Only
                        visible to you
                      </span>
                    </h2>
                    <div className={styles.recapActions}>
                      <IconButton
                        aria-label="Regenerate recap"
                        icon={SyncIcon}
                        size="small"
                        onClick={handleGenerateRecap}
                        disabled={isGeneratingRecap}
                      />
                    </div>
                  </div>
                )}

                {latestRecap.insights && latestRecap.insights.length > 0 && (
                  <div className={styles.insightCard}>
                    <div className={styles.insightHeader}>
                      <h3 className={styles.widgetTitle}>Insights</h3>
                    </div>
                    <ul className={styles.insightList}>
                      {latestRecap.insights.map(insight => (
                        <li key={insight.id} className={styles.insightItem}>
                          <PulseIcon size={16} aria-hidden="true" className={styles.insightIcon} />
                          <span className={styles.insightText}>{insight.content}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {latestRecap.report && reportHtml && (
                  <div className={styles.insightCard}>
                    <div className={styles.reportContent}>
                      <SanitizedMarkdownViewer unverifiedHTML={reportHtml} openLinksInNewTab />
                    </div>
                  </div>
                )}
              </div>
            )}

            {noRecapsAvailable && (
              <div className={styles.insightCard}>
                <Blankslate>
                  <Blankslate.Visual>
                    <BookIcon size="medium" />
                  </Blankslate.Visual>
                  <Blankslate.Heading>Turn recent activity into insights</Blankslate.Heading>
                  <Blankslate.Description>
                    Get a quick summary and actionable insights from recent work in this project.
                  </Blankslate.Description>
                  <Blankslate.PrimaryAction
                    onClick={handleGenerateRecap}
                    disabled={isGeneratingRecap}
                    className={styles.generateRecapLargeButton}
                  >
                    {isGeneratingRecap ? 'Generating recap...' : 'Generate recap'}
                  </Blankslate.PrimaryAction>
                </Blankslate>
              </div>
            )}
          </div>
        )}

        {selectedTab === 'archive' && (
          <div role="tabpanel" id="archive-panel" aria-labelledby="archive-tab">
            {isLoading && <div>Loading recaps...</div>}

            {!isLoading && !error && recaps && recaps.length > 0 ? (
              <div className={styles.archiveContent}>
                <ActionList className={styles.archiveList}>
                  {recaps.map(recap => (
                    <ActionList.Item
                      key={recap.id}
                      onSelect={() => handleViewRecap(recap.id)}
                      {...testIdProps('archive-item')}
                      className={styles.archiveItem}
                    >
                      <div>
                        <h3 className={styles.archiveTitle}>
                          {(() => {
                            const {dateString, showWeekOf} = formatRecapDateRange(recap)
                            return showWeekOf ? `Week of ${dateString}` : `${dateString}`
                          })()}
                        </h3>
                        <div className={styles.archiveDetails}>
                          {recap.description ? recap.description : 'No description available'}
                        </div>
                      </div>
                    </ActionList.Item>
                  ))}
                </ActionList>
              </div>
            ) : (
              !isLoading && !error && <div>No archived recaps available</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

try{ RecapContent.displayName ||= 'RecapContent' } catch {}